import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "../icons.js";
import { Field } from "./Field.js";
export const COMBOBOX_CLOSED = { open: false, query: null, active: -1 };
/** The options whose label or value contains the query, in any case; an empty query, all. */
export function comboboxMatches(options, query) {
    const needle = query.trim().toLowerCase();
    if (needle === "")
        return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle));
}
/** The options listed in a state: all of them until something is typed. */
export function comboboxListed(options, state) {
    return state.query === null ? options : comboboxMatches(options, state.query);
}
/** The combobox's rules as arithmetic: one action against the state, the options and the value. */
export function comboboxStep(state, action, options, value) {
    const listed = comboboxListed(options, state);
    const chosen = options.findIndex((o) => o.value === value);
    const commit = (index) => {
        const option = listed[index];
        return option === undefined
            ? { state, handled: true }
            : { state: COMBOBOX_CLOSED, handled: true, commit: option.value };
    };
    switch (action.type) {
        case "type": {
            const matches = comboboxMatches(options, action.text);
            return { state: { open: true, query: action.text, active: matches.length > 0 ? 0 : -1 }, handled: true };
        }
        case "open":
            return state.open
                ? { state, handled: true }
                : { state: { open: true, query: null, active: chosen >= 0 ? chosen : 0 }, handled: true };
        case "close":
            return { state: COMBOBOX_CLOSED, handled: true };
        case "hover":
            return { state: { ...state, active: action.index }, handled: true };
        case "pick":
            return commit(action.index);
        case "key":
            break;
    }
    const down = action.key === "ArrowDown";
    if (down || action.key === "ArrowUp") {
        if (!state.open) {
            const active = chosen >= 0 ? chosen : down ? 0 : options.length - 1;
            return { state: { open: true, query: null, active }, handled: true };
        }
        if (listed.length === 0)
            return { state, handled: true };
        const from = state.active < 0 ? (down ? -1 : 0) : state.active;
        const active = (from + (down ? 1 : -1) + listed.length) % listed.length;
        return { state: { ...state, active }, handled: true };
    }
    if (!state.open)
        return { state, handled: false };
    if (action.key === "Enter")
        return commit(state.active);
    if (action.key === "Escape")
        return { state: COMBOBOX_CLOSED, handled: true };
    return { state, handled: false };
}
const ROOM = 160;
const GAP = 6;
const MAX_HEIGHT = 264;
/** The list's content: the options, or the No matches row. Each option's id is `{id}-{index}`. */
export function ComboboxListbox({ id, options, active, value, emptyLabel, onPick, onHover }) {
    return (_jsxs(_Fragment, { children: [_jsx("ul", { role: "listbox", id: id, style: { listStyle: "none", margin: 0, padding: 0 }, children: options.map((option, index) => (_jsxs("li", { id: `${id}-${index}`, role: "option", "aria-selected": option.value === value, "data-combobox-option": option.value, "data-active": index === active ? "" : undefined, 
                    // Keeps focus in the input, which the click then commits from.
                    onMouseDown: (event) => event.preventDefault(), onMouseMove: () => {
                        if (index !== active)
                            onHover(index);
                    }, onClick: () => onPick(index), style: {
                        display: "flex", alignItems: "center", gap: "var(--space-1)",
                        padding: "var(--space-075) 10px", borderRadius: "var(--radius-item)",
                        background: index === active ? "var(--tone)" : "transparent",
                        color: "var(--ink-1)", cursor: "pointer", whiteSpace: "nowrap",
                        fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
                    }, children: [_jsx("span", { style: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }, children: option.label }), option.value === value && (_jsx("span", { "aria-hidden": "true", style: { display: "inline-flex", flex: "none" }, children: _jsx(Icon, { icon: Check, size: 16 }) }))] }, option.value))) }), options.length === 0 && (_jsx("div", { "data-combobox-empty": "", role: "status", style: {
                    padding: "var(--space-075) 10px", color: "var(--ink-2)",
                    fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
                }, children: emptyLabel }))] }));
}
export function ComboboxField({ label, options, value, onChange, placeholder, emptyLabel = "No matches", error, note, style, onKeyDown, onBlur, onFocus, onClick, ...input }) {
    const listId = useId();
    const [state, setState] = useState(COMBOBOX_CLOSED);
    const [place, setPlace] = useState(null);
    const root = useRef(null);
    const box = useRef(null);
    const listed = comboboxListed(options, state);
    const chosen = options.find((o) => o.value === value);
    const text = state.query ?? chosen?.label ?? "";
    const act = (action) => {
        const next = comboboxStep(state, action, options, value);
        setState(next.state);
        if (next.commit !== undefined && next.commit !== value)
            onChange(next.commit);
        return next;
    };
    // Placed against the box while open, again on any scroll or resize.
    useLayoutEffect(() => {
        if (!state.open)
            return;
        const measure = () => {
            const rect = box.current?.getBoundingClientRect();
            if (rect === undefined)
                return;
            const below = window.innerHeight - rect.bottom - GAP * 2;
            const above = rect.top - GAP * 2;
            setPlace(below >= ROOM || below >= above
                ? { left: rect.left, width: rect.width, top: rect.bottom + GAP, maxHeight: Math.min(MAX_HEIGHT, below) }
                : { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(MAX_HEIGHT, above) });
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [state.open]);
    // A press outside the field and its list closes it.
    useEffect(() => {
        if (!state.open)
            return;
        const outside = (event) => {
            if (!root.current?.contains(event.target))
                setState(COMBOBOX_CLOSED);
        };
        document.addEventListener("pointerdown", outside, true);
        return () => document.removeEventListener("pointerdown", outside, true);
    }, [state.open]);
    // The active option stays in view as the keys move it.
    useEffect(() => {
        if (state.open && state.active >= 0) {
            document.getElementById(`${listId}-${state.active}`)?.scrollIntoView?.({ block: "nearest" });
        }
    }, [state.open, state.active, listId]);
    return (_jsx(Field, { label: label, error: error, note: note, children: (props, invalid) => (_jsxs("div", { ref: root, style: { position: "relative" }, children: [_jsxs("div", { ref: box, "data-field": "", "data-combobox": "", "data-invalid": invalid ? "" : undefined, style: {
                        display: "flex", alignItems: "center", position: "relative", height: 36,
                        borderRadius: "var(--radius-control)",
                        background: invalid ? "var(--sheet)" : "var(--tone)",
                        boxShadow: invalid ? "inset 0 0 0 1.5px var(--problem)" : "none",
                        ...style,
                    }, children: [_jsx("input", { autoComplete: "off", spellCheck: false, ...input, ...props, type: "text", role: "combobox", "aria-autocomplete": "list", "aria-expanded": state.open, "aria-controls": state.open ? listId : undefined, "aria-activedescendant": state.open && state.active >= 0 ? `${listId}-${state.active}` : undefined, value: text, placeholder: placeholder, onChange: (event) => act({ type: "type", text: event.target.value }), onKeyDown: (event) => {
                                onKeyDown?.(event);
                                if (event.defaultPrevented)
                                    return;
                                const next = act({ type: "key", key: event.key });
                                if (next.handled) {
                                    // Stopped here: Return does not submit the form and Escape does not reach
                                    // the dialog (prevented, so a native `<dialog>` does not cancel).
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                            }, onClick: (event) => {
                                onClick?.(event);
                                act({ type: "open" });
                            }, onFocus: (event) => {
                                onFocus?.(event);
                                // Typing replaces the chosen label rather than appending to it.
                                event.currentTarget.select();
                            }, onBlur: (event) => {
                                onBlur?.(event);
                                setState(COMBOBOX_CLOSED);
                            }, style: {
                                flex: 1, minWidth: 0, height: "100%", background: "transparent", border: "none", outline: "none",
                                padding: "0 36px 0 var(--space-15)", color: "var(--ink-1)", fontFamily: "var(--font-sans)",
                                fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
                            } }), _jsx("span", { "aria-hidden": "true", style: { position: "absolute", right: "var(--space-15)", display: "inline-flex", color: "var(--ink-1)", pointerEvents: "none" }, children: _jsx(Icon, { icon: ChevronDown, size: 16 }) })] }), state.open && (_jsx("div", { "data-combobox-list": "", style: {
                        position: "fixed", zIndex: 1, left: place?.left, width: place?.width, top: place?.top,
                        bottom: place?.bottom, maxHeight: place?.maxHeight ?? MAX_HEIGHT, overflowY: "auto",
                        visibility: place === null ? "hidden" : undefined,
                        padding: "var(--space-075)", background: "var(--float)",
                        borderRadius: "var(--radius-panel)", boxShadow: "var(--shadow-menu)",
                    }, children: _jsx(ComboboxListbox, { id: listId, options: listed, active: state.active, value: value, emptyLabel: emptyLabel, onPick: (index) => act({ type: "pick", index }), onHover: (index) => act({ type: "hover", index }) }) }))] })) }));
}
